import React, { useState, useEffect, useRef } from "react";
import mqtt from "mqtt";

export default function DashboardContent({ isDarkMode, devices = [], onDownloadCode, onShowCharts }) {
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [mqttClient, setMqttClient] = useState(null);
  const [deviceValues, setDeviceValues] = useState({});
  const previousTopic = useRef(null);

  const API_MQTT = "https://holmio.net/app/api_mqtt.php";
  const bgColorClass = isDarkMode ? "bg-gray-900" : "bg-gray-100";
  const cardClass = isDarkMode ? "bg-gray-800 text-white" : "bg-white text-gray-800";

  // ✅ Unidades comunes
  const subtopicUnits = {
    temperature: "°C",
    humidity: "%",
  };

  // ✅ Función para colores dinámicos
  const getDynamicStyle = (value, type) => {
    const val = parseFloat(value);
    if (isNaN(val)) return "bg-gray-100 text-gray-800";

    if (type === "temperature") {
      if (val < 18) return "bg-blue-100 text-blue-800";
      if (val > 28) return "bg-red-100 text-red-800";
      return "bg-green-100 text-green-800";
    }

    if (type === "humidity") {
      if (val < 30) return "bg-yellow-100 text-yellow-800";
      if (val > 60) return "bg-blue-100 text-blue-800";
      return "bg-green-100 text-green-800";
    }

    return "bg-gray-100 text-gray-800";
  };

  // ✅ Inicializar cliente MQTT
  useEffect(() => {
    const initClient = async () => {
      if (!mqttClient) {
        try {
          const res = await fetch(API_MQTT);
          const cfg = await res.json();
          const url = `wss://${cfg.host}:${cfg.port_web}/mqtt`;

          const client = mqtt.connect(url, {
            username: cfg.user,
            password: cfg.pass,
            clientId: `holmio_dashboard_${Math.random().toString(16).slice(2, 10)}`,
            keepalive: 60,
            reconnectPeriod: 3000,
            clean: true,
          });

          client.on("connect", () => console.log("✅ Cliente MQTT conectado"));
          client.on("message", (topic, message) => {
            const val = message.toString();
            const parts = topic.split("/");
            const control = parts[2] || "";
            const subtopic = parts[3] || "value";

            const key =
              subtopic === "data" || subtopic === "state"
                ? control
                : `${control}.${subtopic}`;

            setDeviceValues((prev) => ({ ...prev, [key]: val }));
          });

          setMqttClient(client);
        } catch (error) {
          console.error("Error conectando MQTT:", error);
        }
      }
    };

    initClient();

    return () => {
      if (mqttClient) {
        console.log("🔴 Cerrando conexión MQTT...");
        mqttClient.end(true);
        setMqttClient(null);
      }
    };
  }, []);

  // ✅ Manejo de suscripción dinámica al cambiar dispositivo
  useEffect(() => {
    if (mqttClient && mqttClient.connected) {
      const newTopic = selectedDevice ? `home/${selectedDevice.serial}/#` : null;

      if (previousTopic.current && previousTopic.current !== newTopic) {
        mqttClient.unsubscribe(previousTopic.current, () => {
          console.log(`🔴 Desuscrito: ${previousTopic.current}`);
        });
      }

      if (newTopic) {
        mqttClient.subscribe(newTopic, (err) => {
          if (!err) {
            console.log(`📡 Suscrito a: ${newTopic}`);
            previousTopic.current = newTopic;
          }
        });
      }
    }
  }, [mqttClient, selectedDevice]);

  // ✅ Publicar comandos MQTT
  const handleControlAction = (control, value) => {
    if (!mqttClient) return;
    const topic = `home/${selectedDevice.serial}/${control.description}/control`;
    mqttClient.publish(topic, value);
    console.log(`✅ Publicado en ${topic}: ${value}`);
  };

  const filteredDevices = devices.filter((d) =>
    d.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (selectedDevice) {
    return (
      <div className="p-6">
        {/* Header */}
        <div className="flex justify-between mb-6">
          <h2 className="text-2xl font-semibold">{selectedDevice.name}</h2>
          <button
            onClick={() => {
              setSelectedDevice(null);
              setDeviceValues({});
            }}
            className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            Volver
          </button>
        </div>

        {/* Info general */}
        <div className="mb-6">
          <p><strong>Serial:</strong> {selectedDevice.serial}</p>
          <p><strong>Tipo:</strong> {selectedDevice.type === "custom" ? "Custom" : "Homologado"}</p>
        </div>

        {/* Layout principal */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Controles Remotos */}
          <div className={`${cardClass} p-6 rounded shadow`}>
            <h3 className="font-bold mb-4 text-lg">Controles Remotos</h3>
            {selectedDevice.controls.length > 0 ? (
              selectedDevice.controls.map((ctrl, idx) => {
                const ctrlKey = ctrl.description.trim();
                const state = deviceValues[ctrlKey] || "OFF";

                return (
                  <div key={idx} className="flex justify-between items-center border-b py-3">
                    <span className="font-semibold">{ctrl.description || ctrl.control_name}</span>
                    {ctrl.control_name === "switch" ? (
                      <button
                        onClick={() => handleControlAction(ctrl, state === "ON" ? "OFF" : "ON")}
                        className={`px-4 py-2 rounded text-white transition-all duration-300 transform ${
                          state === "ON"
                            ? "bg-green-500 hover:bg-green-600 scale-105"
                            : "bg-gray-500 hover:bg-gray-600"
                        }`}
                      >
                        {state}
                      </button>
                    ) : ctrl.control_name === "slider" ? (
                      <input
                        type="range"
                        min="0"
                        max="100"
                        defaultValue={deviceValues[ctrlKey] || 0}
                        className="w-32 accent-blue-500 transition-all duration-300"
                        onChange={(e) => handleControlAction(ctrl, e.target.value)}
                      />
                    ) : (
                      <span className="text-gray-400">Solo lectura</span>
                    )}
                  </div>
                );
              })
            ) : (
              <p>No hay controles disponibles.</p>
            )}
          </div>

          {/* Datos en Tiempo Real */}
          <div className={`${cardClass} p-6 rounded shadow`}>
            <h3 className="font-bold mb-4 text-lg">Datos en Tiempo Real</h3>
            <div className="grid grid-cols-2 gap-4">
              {selectedDevice.controls.map((ctrl) => {
                const description = ctrl.description || ctrl.control_name;
                const ctrlKey = ctrl.description.trim();

                const keys = Object.keys(deviceValues).filter((k) =>
                  k.startsWith(ctrlKey)
                );

                const displayKeys = keys.filter((k) => !k.endsWith(".control"));

                if (displayKeys.length === 1 && displayKeys[0].endsWith(".data")) {
                  const k = displayKeys[0];
                  return (
                    <div
                      key={k}
                      className={`border rounded p-4 text-center shadow transition-all duration-300 hover:scale-105 ${getDynamicStyle(deviceValues[k], "data")}`}
                    >
                      <p className="text-sm font-semibold">{description}</p>
                      <p className="text-3xl font-extrabold">{deviceValues[k]}</p>
                      <p className="text-xs">{ctrl.unit || ""}</p>
                    </div>
                  );
                }

                if (displayKeys.length > 1) {
                  return displayKeys.map((k) => (
                    <div
                      key={k}
                      className={`border rounded p-4 text-center shadow transition-all duration-300 hover:scale-105 ${getDynamicStyle(deviceValues[k], k.split(".")[1])}`}
                    >
                      <p className="text-sm font-semibold">{`${description} (${k.split(".")[1]})`}</p>
                      <p className="text-3xl font-extrabold">{deviceValues[k]}</p>
                      <p className="text-xs">{subtopicUnits[k.split(".")[1]] || ""}</p>
                    </div>
                  ));
                }

                return (
                  <div
                    key={ctrl.description}
                    className="border rounded p-4 text-center shadow transition-all duration-300 hover:scale-105 ${getDynamicStyle(deviceValues[ctrlKey], 'data')}"
                  >
                    <p className="text-sm font-semibold">{description}</p>
                    <p className="text-3xl font-extrabold">
                      {deviceValues[ctrlKey] || "--"}
                    </p>
                    <p className="text-xs">{ctrl.unit || ""}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Gráficos */}
        <div className={`${cardClass} p-6 rounded shadow`}>
          <h3 className="font-bold mb-4 text-lg">Histórico</h3>
          <button
            onClick={() => onShowCharts(selectedDevice)}
            className="bg-green-500 text-white px-4 py-2 rounded"
          >
            Ver Gráficos
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`p-6 ${bgColorClass} min-h-screen`}>
      <div className="flex justify-between items-center mb-8">
        <h2 className="text-3xl font-bold">Mis Dispositivos</h2>
        <input
          type="text"
          placeholder="Buscar dispositivo..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="border px-4 py-2 rounded w-64"
        />
      </div>

      {filteredDevices.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredDevices.map((device) => (
            <div
              key={device.id}
              className={`${cardClass} p-6 rounded shadow hover:shadow-xl`}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold">{device.name}</h3>
                <span
                  className={`text-xs px-2 py-1 rounded ${
                    device.type === "custom"
                      ? "bg-purple-600 text-white"
                      : "bg-blue-500 text-white"
                  }`}
                >
                  {device.type === "custom" ? "Custom" : "Homologado"}
                </span>
              </div>
              <p className="text-sm mb-4">{device.board || "Sin board"}</p>
              <div className="flex justify-between">
                <button
                  onClick={() => setSelectedDevice(device)}
                  className="bg-blue-500 text-white px-4 py-2 rounded text-sm"
                >
                  Detalles
                </button>
                <button
                  onClick={() => onDownloadCode(device)}
                  className="bg-green-500 text-white px-4 py-2 rounded text-sm"
                >
                  Código
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p>No hay dispositivos.</p>
      )}
    </div>
  );
}
